/**
 * Client for `POST /api/intents/floor-checkout` (L5, tv-54xv): the Floor's
 * make-sale checkout as ONE server transaction — the booking completed, the sale
 * recorded and its cash added to the till together, or none of it. The sale
 * (`sale-<booking>`) is the idempotency key, so a retry or a re-run answers
 * `alreadyApplied` and changes nothing.
 *
 * Same contract as the other intent clients: never throws, sends the sim clock as
 * `nowIso`, and returns a discriminated result whose failure `kind` the caller
 * branches on ('rejected' = the server refused it; 'transient' = safe to retry).
 */
import { authHeaders, isSigningIn, whenSignedIn } from '@/api/persist';
import { intentErrorKind, type IntentFailure } from '@/api/intents';
import { useSimulationStore } from '@/state/simulationStore';
import type {
  CashLedger,
  Deposit,
  DepositLedgerEntry,
  RebookAttribution,
  RebookOutcome,
  SalePayment,
} from '@/domain/types';

export interface FloorCheckoutPayload {
  bookingId: string;
  checkout: {
    amountPaidAud: number;
    paymentMethod: string;
    cameDown?: string;
    checkedOutBy?: string;
    sessionNotes?: string;
    rolledDepositId?: string;
  };
  sale: SalePayment;
  /** The piece finished today: the server redeems the whole deposit (14ym9bvbgg3 c). */
  pieceCompleted?: boolean;
  /** The in-chair rebook read, stamped on the booking by the server. */
  rebook?: { outcome: RebookOutcome; attribution?: RebookAttribution };
  nowIso?: string;
}

/** A deposit the checkout settled (a pay-at-studio hold) or redeemed, as the server holds it. */
export interface ServerCheckoutDeposit {
  id: string;
  status: Deposit['status'];
  amountPaid?: number;
  paidAt?: string;
  bookingId?: string;
  heldBookingId?: null;
  method?: Deposit['method'];
  ledgerAppended: DepositLedgerEntry[];
}

/** What the server committed, for the page to mirror without a PUT back. */
export interface ServerCheckoutEcho {
  sale?: SalePayment;
  booking?: { amountPaidAud: number; depositId?: string };
  deposits?: ServerCheckoutDeposit[];
  healingCheckId?: string;
  /** Pay-over-time plans the checkout closed (ontheday-7 a). */
  topupPlansClosed?: string[];
  at?: string;
}

/** The day ledger's figures as the server holds them after the checkout. */
export type ServerLedgerFigures = Pick<
  CashLedger,
  'id' | 'studioId' | 'dayIso' | 'openingFloatAud' | 'cashInAud' | 'payoutsAud' | 'floatDipped' | 'countedAud'
>;

export type FloorCheckoutResult =
  | ({ ok: true; alreadyApplied: boolean; cashLedger?: ServerLedgerFigures } & ServerCheckoutEcho)
  | IntentFailure;

export async function floorCheckoutIntent(payload: FloorCheckoutPayload): Promise<FloorCheckoutResult> {
  if (isSigningIn()) await whenSignedIn(); // never on a previous persona's token mid-switch
  let res: Response;
  try {
    res = await fetch('/api/intents/floor-checkout', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...payload, nowIso: payload.nowIso ?? useSimulationStore.getState().clockIso }),
    });
  } catch {
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }
  const body = (await res.json().catch(() => undefined)) as
    | ({ alreadyApplied?: unknown; cashLedger?: ServerLedgerFigures; error?: string; code?: string; message?: string } & ServerCheckoutEcho)
    | undefined;
  if (res.ok) {
    return {
      ok: true,
      alreadyApplied: body?.alreadyApplied === true,
      ...(body?.cashLedger ? { cashLedger: body.cashLedger } : {}),
      ...(body?.sale ? { sale: body.sale } : {}),
      ...(body?.booking ? { booking: body.booking } : {}),
      ...(Array.isArray(body?.deposits) ? { deposits: body.deposits } : {}),
      ...(body?.healingCheckId ? { healingCheckId: body.healingCheckId } : {}),
      ...(Array.isArray(body?.topupPlansClosed) ? { topupPlansClosed: body.topupPlansClosed } : {}),
      ...(body?.at ? { at: body.at } : {}),
    };
  }
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
  };
}

/** A one-off session's photo as the session-photos intent files it (ontheday-2). */
export interface SessionPhotoPayload {
  id: string;
  url: string;
  capturedAt: string;
  caption?: string;
  kind?: 'progress' | 'reference';
}

/** POST /api/intents/session-photos — file a one-off session's photos on its booking. Never throws. */
export async function sessionPhotosIntent(
  bookingId: string,
  photos: SessionPhotoPayload[],
): Promise<{ ok: true; added: string[] } | IntentFailure> {
  if (isSigningIn()) await whenSignedIn();
  let res: Response;
  try {
    res = await fetch('/api/intents/session-photos', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ bookingId, photos }),
    });
  } catch {
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }
  const body = (await res.json().catch(() => undefined)) as
    | { added?: string[]; error?: string; code?: string; message?: string }
    | undefined;
  if (res.ok) return { ok: true, added: Array.isArray(body?.added) ? body.added : [] };
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(body?.message ? { message: body.message } : {}),
  };
}
